import { api, unwrap, toPaginated, cleanParams } from "../lib/axios";
import type { ApiEnvelope, ListParams, PaginatedResult } from "../types/api";
import type { Trip, TripStats } from "../types/domain";

export interface TripListParams extends ListParams {
  status?: string;
  startDate?: string;
  endDate?: string;
  driverID?: string;
  passengerID?: string;
  /** Part wallet / part cash trips: all of them, or those awaiting cash. */
  partPayment?: "any" | "unconfirmed";
}

export interface TripMessage {
  _id: string;
  senderType?: string;
  messageType?: string;
  message?: string;
  predefinedKey?: string;
  createdAt?: string;
}

export type DispatchOutcome =
  | "assigned"
  | "searching"
  | "no_driver_found"
  | "cancelled_before_assignment";

export interface DispatchOffer {
  order: number;
  driverId: string | null;
  name: string | null;
  phoneNumber: string | null;
  vehicle: string | null;
  plate: string | null;
  status: "accepted" | "rejected" | "timeout" | "pending" | "unreachable";
  sentAt: string | null;
  respondedAt: string | null;
  /** Seconds from offer to the driver's accept/reject; null for timeouts and admin removals. */
  responseSeconds: number | null;
  removedByAdmin: boolean;
  /** Accepted, then cancelled before pickup; the trip was re-matched. */
  cancelledAfterAccept?: boolean;
  cancelledAt?: string | null;
  cancelReason?: string | null;
}

export interface TripDispatch {
  tripId: string;
  tripStatus: string;
  outcome: DispatchOutcome;
  requestedAt: string | null;
  searchStartedAt: string | null;
  assignedAt: string | null;
  secondsToAssign: number | null;
  summary: {
    offered: number;
    accepted: number;
    rejected: number;
    timedOut: number;
    unreachable?: number;
    pending: number;
    avgResponseSeconds: number | null;
  };
  offers: DispatchOffer[];
  statusHistory: { status: string; at: string }[];
  cancel: { at: string; by: string | null; reason: string | null } | null;
}

export interface TripTrailPoint {
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  tripStatus?: string;
  source: "gps" | "simulation";
  recordedAt: string;
}

export const tripsApi = {
  async dispatch(id: string): Promise<TripDispatch> {
    const res = await api.get<ApiEnvelope<TripDispatch>>(`/admin/trips/${id}/dispatch`);
    return unwrap(res);
  },

  async trail(id: string): Promise<TripTrailPoint[]> {
    const res = await api.get<ApiEnvelope<{ points: TripTrailPoint[] }>>(`/admin/trips/${id}/trail`);
    return unwrap(res).points;
  },

  async list(params: TripListParams): Promise<PaginatedResult<Trip>> {
    const res = await api.get<ApiEnvelope<unknown>>("/trip", {
      params: cleanParams(params),
    });
    return toPaginated<Trip>(res, "trips");
  },

  async messages(id: string): Promise<TripMessage[]> {
    const res = await api.get<ApiEnvelope<TripMessage[] | { messages: TripMessage[] }>>(
      `/trip/${id}/messages`,
    );
    const data = unwrap(res);
    return Array.isArray(data) ? data : (data?.messages ?? []);
  },

  async get(id: string): Promise<Trip> {
    const res = await api.get<ApiEnvelope<Trip>>(`/trip/${id}`);
    return unwrap(res);
  },

  async stats(
    params: Record<string, string | number | undefined> = {},
  ): Promise<TripStats> {
    const res = await api.get<ApiEnvelope<TripStats>>("/trip/stats", {
      params: cleanParams(params),
    });
    return unwrap(res);
  },
};
